/**
 * Converted Leads tab for the GHL Qualifier Location Detail page.
 */

import { useMemo } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Search } from 'lucide-react'
import { QualifierStatCards } from './QualifierStatCards'
import { ConvertedLeadsTable } from './ConvertedLeadsTable'
import { QualifierPagination } from './QualifierPagination'
import type {
  ConvertedLeadFeedRow,
  QualifierStats,
} from '@/hooks/useGHLQualifierDashboard'

interface Props {
  readonly stats: QualifierStats | null | undefined
  readonly statsLoading: boolean
  readonly data: readonly ConvertedLeadFeedRow[]
  readonly count: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly statusFilter: string
  readonly searchTerm: string
  readonly onPageChange: (page: number) => void
  readonly onStatusChange: (status: string) => void
  readonly onSearchChange: (term: string) => void
  readonly onRowClick: (id: string) => void
  /** When true, the location delivers directly to Google Ads — show the per-row "Test upload" button. */
  readonly showTestButton?: boolean
  /** When true, render a per-row Google Ads upload status badge. */
  readonly showUploadStatus?: boolean
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'completed_converted', label: 'Confirmed' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'completed_exhausted', label: 'Exhausted' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'failed', label: 'Failed' },
] as const

export function ConvertedLeadsTab({
  stats,
  statsLoading,
  data,
  count,
  page,
  pageSize,
  isLoading,
  statusFilter,
  searchTerm,
  onPageChange,
  onStatusChange,
  onSearchChange,
  onRowClick,
  showTestButton,
  showUploadStatus,
}: Props) {
  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    if (!term) return data
    return data.filter((row) => {
      const haystack = [
        row.contact_name,
        row.contact_email,
        row.contact_phone,
        row.contact_id,
        row.opportunity_id,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(term)
    })
  }, [data, searchTerm])

  return (
    <div className="space-y-4">
      <QualifierStatCards mode="converted" stats={stats} isLoading={statsLoading} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Select value={statusFilter} onValueChange={onStatusChange}>
            <SelectTrigger className="w-[180px] h-9" aria-label="Filter by status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">Auto-refreshing every 15 seconds</p>
        </div>
        <div className="relative w-72">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search contacts"
            className="pl-9"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label="Search contacts"
          />
        </div>
      </div>

      <ConvertedLeadsTable
        data={filtered}
        isLoading={isLoading}
        onRowClick={onRowClick}
        showTestButton={showTestButton}
        showUploadStatus={showUploadStatus}
      />

      <QualifierPagination
        page={page}
        pageSize={pageSize}
        count={count}
        onPageChange={onPageChange}
      />
    </div>
  )
}
